import { useEffect, useRef, useState } from 'react'

function secondsLeft(untilMs: number | null): number {
  if (untilMs === null) return 0
  return Math.max(0, Math.ceil((untilMs - Date.now()) / 1000))
}

/**
 * Whole seconds until a local-clock instant, re-rendering once a second until
 * it reaches 0. Null means nothing to count and reads 0.
 *
 * The value is read off the clock on every render rather than kept in state,
 * so the first render after `untilMs` changes is already right. A stale 0 for
 * that one frame would let LoginPage's expiry effect clear a lockout it had
 * only just received. The interval exists purely to force re-renders; it is
 * cleared at 0, when `untilMs` changes and on unmount, so an idle page does
 * not tick for nothing.
 */
export function useCountdown(untilMs: number | null): number {
  const [, setTick] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (secondsLeft(untilMs) === 0) return
    timerRef.current = setInterval(() => {
      setTick((t) => t + 1)
      if (secondsLeft(untilMs) === 0 && timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }, 1000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [untilMs])

  return secondsLeft(untilMs)
}
